/**
 * Bill of Sale PDF Generator — OMVIC Form
 * 
 * Fills the OMVIC Bill of Sale PDF template (based on 2026-06-09-bill.pdf)
 * with deal data from the CRM using pdf-lib.
 * 
 * Field names are clean & semantic (e.g. vehicle_make, vin_0, settlement_selling_price).
 * Font sizes match the reference standard: ~8pt body, 10pt VIN, 14pt total.
 */
import { PDFDocument } from 'pdf-lib';

// ── Types ──────────────────────────────────────────────────────────────────────

interface BillOfSaleCustomer {
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
}

interface BillOfSaleVehicle {
  year: number;
  make: string;
  model: string;
  color: string;
  vin: string;
  mileage: number;
  body_type?: string;
  trim?: string;
  stock_number?: string;
}

interface BillOfSaleWorksheet {
  selling_price: number;
  omvic_fee: number;
  hst_rate: number;
  license_fee: number;
  gasoline: number;
  deposit_amount: number;
  deposit_method: string;
  lien_registration_fee: number;
  rst_on_insurance: number;
  warranties: { name: string; amount: number }[];
  fees: { name: string; amount: number }[];
  accessories: { name: string; amount: number }[];
  insurances: { name: string; amount: number }[];
}

interface BillOfSaleTradeIn {
  has_trade_in: boolean;
  year: string;
  make: string;
  model: string;
  body_type: string;
  trim_level: string;
  colour: string;
  vin: string;
  exact_distance: string;
  distance_type: 'kms' | 'miles';
  hst_registrant_no: string;
  has_lien: boolean;
  lien_holder: string;
  lien_amount: number;
  trade_in_allowance: number;
}

interface BillOfSaleFinancing {
  is_financing: boolean;
  financing_type: 'third_party' | 'dealer' | '';
  date_of_advance: string;
  interest_start_date: string;
  regular_payments: number;
  fixed_rate: number;
  term_of_loan: number;
  amortization_period: number;
  total_number_of_payments: number;
  payment_frequency: string;
  date_of_first_payment: string;
  date_of_last_payment_amount: string;
  remaining_balance: number;
  total_of_all_payments: number;
  total_interest: number;
  apr: number;
  dealer_incentive: boolean;
}

interface BillOfSaleGuaranty {
  has_dealer_guaranty: boolean;
  days_or_km: string;
  description: string;
  has_extended_warranty: boolean;
  retail_value: number;
  company: string;
  months_or_km: string;
  warranty_description: string;
}

interface BillOfSaleInsurance {
  company: string;
  policy_no: string;
  expiry_date: string;
  agent_and_phone: string;
}

interface BillOfSaleDelivery {
  delivery_date: string;
  delivery_notes: string;
  is_delivered: boolean;
  mfrs_warranty_date: string;
  safety_certificate: boolean;
  daily_rental: boolean;
  mto_brand: string;
}

interface BillOfSaleComputedTotals {
  extWarrantyTotal: number;
  feesTotal: number;
  accessoriesTotal: number;
  insurancesTotal: number;
  subTotal1: number;
  netDifference: number;
  hstAmount: number;
  payoutLien: number;
  subTotal2: number;
  payableOnDelivery: number;
  totalPrincipalLoan: number;
  costOfBorrowing: number;
  totalBalanceDue: number;
}

export interface BillOfSaleData {
  customer: BillOfSaleCustomer;
  vehicle: BillOfSaleVehicle | null;
  worksheet: BillOfSaleWorksheet;
  tradeIn: BillOfSaleTradeIn;
  financing: BillOfSaleFinancing;
  guaranty: BillOfSaleGuaranty;
  insurance: BillOfSaleInsurance;
  delivery: BillOfSaleDelivery;
  computed: BillOfSaleComputedTotals;
  comments: string;
  dealDate?: string; // ISO date string
  salespersonName?: string;
  salespersonRegNo?: string;
  driverLicense?: string;
  driverLicenseExpiry?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatMoney(amount: number): string {
  if (!amount && amount !== 0) return '';
  return '$' + amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatMoneyOrBlank(amount: number): string {
  if (!amount) return '';
  return formatMoney(amount);
}

function padDigits(value: string | number, length: number): string[] {
  const str = String(value).replace(/\D/g, '');
  return str.padStart(length, '').split('').map(c => c === ' ' ? '' : c);
}

function formatDate(dateStr: string, isNarrow: boolean = false): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const day = String(d.getDate()).padStart(2, '0');
    const yearFull = String(d.getFullYear());
    
    if (isNarrow) {
      const yearShort = yearFull.slice(-2);
      return `${month} ${day}/${yearShort}`;
    }
    return `${month}. ${day}/${yearFull}`;
  } catch {
    return dateStr;
  }
}

function safeSetText(form: any, fieldName: string, value: string, fontSize?: number) {
  try {
    const field = form.getTextField(fieldName);
    field.setText(value || '');
    
    let finalFontSize = fontSize;
    
    if (!finalFontSize) {
      if (
        fieldName.startsWith('vin_') || 
        fieldName.startsWith('distance_') || 
        fieldName.startsWith('trade_in_vin_') || 
        fieldName.startsWith('trade_in_distance_') ||
        fieldName === 'overall_day' ||
        fieldName === 'overall_month' ||
        fieldName === 'overall_year'
      ) {
        // Keep template defaults for single-character digit boxes and date headers
        return;
      }

      // ── Fixed font sizes for settlement fields (consistent across all rows) ──
      const isSettlementField = fieldName.startsWith('settlement_');
      if (isSettlementField) {
        if (fieldName === 'settlement_total_balance_due') {
          finalFontSize = 11;
        } else {
          finalFontSize = 8;
        }
      } else if (fieldName.startsWith('comments_')) {
        finalFontSize = 8;
      } else if (fieldName === 'vehicle_stock') {
        finalFontSize = 7.5;
      } else {
        // Dynamic sizing for non-settlement fields
        const widgets = field.acroField.getWidgets();
        if (widgets && widgets.length > 0) {
          const rect = widgets[0].getRectangle();
          const H = rect.height;
          const W = rect.width;
          
          // Height constraint: prevent vertical clipping by making font size at most 88% of height
          const maxFontByHeight = H * 0.88;
          
          // Width constraint: estimate needed font size so that the string fits within width
          let maxFontByWidth = 10;
          if (value) {
            const L = value.length;
            // Standard average character width factor is ~0.55-0.6, but we use 0.72
            // to safely accommodate wide uppercase characters, numbers, and symbols.
            const avgCharWidthFactor = 0.72;
            maxFontByWidth = W / (L * avgCharWidthFactor);
          }
          
          const limit = 10;
          
          // Select the minimum constraint, but ensure we don't go below 5.5pt for readability
          finalFontSize = Math.max(5.5, Math.min(maxFontByHeight, maxFontByWidth, limit));
        } else {
          finalFontSize = 10;
        }
      }
    }
    
    field.setFontSize(finalFontSize);
  } catch {
    // Field not found — skip silently
  }
}

function safeSetCheckbox(form: any, fieldName: string, checked: boolean) {
  try {
    const field = form.getCheckBox(fieldName);
    if (checked) field.check();
    else field.uncheck();
  } catch {
    // Field not found — skip silently
  }
}

// ── Main Generator ─────────────────────────────────────────────────────────────

export async function generateBillOfSale(data: BillOfSaleData): Promise<Uint8Array> {
  // Load the PDF template
  const templateUrl = `/templates/omvic-bill-of-sale.pdf?v=${Date.now()}`;
  const response = await fetch(templateUrl);
  if (!response.ok) throw new Error('Failed to load Bill of Sale PDF template');
  const templateBytes = await response.arrayBuffer();
  
  const pdfDoc = await PDFDocument.load(templateBytes);
  const form = pdfDoc.getForm();

  const { customer, vehicle, worksheet, tradeIn, financing, guaranty, insurance, delivery, computed, comments } = data;

  // ── Deal Date ──

  const dealDate = data.dealDate ? new Date(data.dealDate) : new Date();
  safeSetText(form, 'overall_day', String(dealDate.getDate()));
  safeSetText(form, 'overall_month', String(dealDate.getMonth() + 1));
  safeSetText(form, 'overall_year', String(dealDate.getFullYear()));

  // ── Purchaser (Customer) Info ──

  const fullName = `${customer.first_name} ${customer.last_name}`.trim();
  safeSetText(form, 'fullName', fullName);
  safeSetText(form, 'address', customer.address || '');
  safeSetText(form, 'city', customer.city || '');
  safeSetText(form, 'province', customer.province || '');
  safeSetText(form, 'postal', customer.postal_code || '');
  safeSetText(form, 'phone_business', customer.phone || '');
  safeSetText(form, 'phone_home', '');
  safeSetText(form, 'email', customer.email || '');

  // Driver's License
  safeSetText(form, 'license', data.driverLicense || '');
  safeSetText(form, 'license_expiry', data.driverLicenseExpiry || '');

  // ── Vehicle Info ──

  if (vehicle) {
    safeSetText(form, 'vehicle_year', String(vehicle.year));
    safeSetText(form, 'vehicle_make', vehicle.make.toUpperCase());
    safeSetText(form, 'vehicle_model', vehicle.model.toUpperCase());
    safeSetText(form, 'vehicle_body_type', (vehicle.body_type || '').toUpperCase());
    safeSetText(form, 'vehicle_trim_level', (vehicle.trim || '').toUpperCase());
    safeSetText(form, 'vehicle_colour', vehicle.color.toUpperCase());
    safeSetText(form, 'vehicle_stock', vehicle.stock_number || '');

    // VIN — sequential vin_0 through vin_16 (clean field names!)
    const vinChars = vehicle.vin.toUpperCase().split('');
    for (let i = 0; i < 17; i++) {
      safeSetText(form, `vin_${i}`, vinChars[i] || '');
    }

    // Distance (odometer) — 6 character boxes
    const distDigits = padDigits(vehicle.mileage, 6);
    for (let i = 0; i < 6; i++) {
      safeSetText(form, `distance_${i}`, distDigits[i]);
    }

    // Distance as text + date
    safeSetText(form, 'vehicle_travelled_distance', String(vehicle.mileage));
    safeSetText(form, 'vehicle_travelled_date', formatDate(data.dealDate || new Date().toISOString(), true));

    // Distance type checkboxes
    safeSetCheckbox(form, 'checkbox_vehicle_distance_kms', true);
    safeSetCheckbox(form, 'checkbox_vehicle_distance_miles', false);
    safeSetCheckbox(form, 'checkbox_vehicle_distance_had_travelled', true);
  }

  // ── Delivery & Disclosures ──

  safeSetText(form, 'delivery_date', formatDate(delivery.delivery_date, true));
  safeSetText(form, 'delivery_details', delivery.delivery_notes);
  safeSetText(form, 'warrenty_date', formatDate(delivery.mfrs_warranty_date, true));
  safeSetText(form, 'vehicle_mto_brand', delivery.mto_brand || 'None');

  // Safety certificate
  safeSetCheckbox(form, 'checkbox_vehicle_safety_yes', delivery.safety_certificate);
  safeSetCheckbox(form, 'checkbox_vehicle_safety_no', !delivery.safety_certificate);

  // Daily rental
  safeSetCheckbox(form, 'checkbox_vehicle_rental', delivery.daily_rental);

  // ── Insurance ──

  safeSetText(form, 'insurance_company', insurance.company);
  safeSetText(form, 'insurance_policy', insurance.policy_no);
  safeSetText(form, 'insurance_expiry', insurance.expiry_date);
  safeSetText(form, 'insurance_agent', insurance.agent_and_phone);

  // ── Dealer Guaranty ──

  safeSetCheckbox(form, 'checkbox_guaranty_yes', guaranty.has_dealer_guaranty);
  safeSetCheckbox(form, 'checkbox_guaranty_no', !guaranty.has_dealer_guaranty);

  if (guaranty.has_dealer_guaranty) {
    safeSetText(form, 'guaranty_days', guaranty.days_or_km);
    safeSetText(form, 'guaranty_description_1', guaranty.description);
  }

  // ── Extended Warranty ──

  safeSetCheckbox(form, 'checkbox_warantee_yes', guaranty.has_extended_warranty);
  safeSetCheckbox(form, 'checkbox_warantee_no', !guaranty.has_extended_warranty);

  if (guaranty.has_extended_warranty) {
    safeSetText(form, 'extended_warranty_company', guaranty.company);
    safeSetText(form, 'extended_warranty_months', guaranty.months_or_km);
    safeSetText(form, 'extended_warranty_selling_price', guaranty.retail_value ? String(guaranty.retail_value) : '');
    safeSetText(form, 'extended_warranty_description_1', guaranty.warranty_description);
  }

  // ── Terms of Settlement (Worksheet) ──

  safeSetText(form, 'settlement_selling_price', formatMoney(worksheet.selling_price));
  safeSetText(form, 'settlement_extended_warranty', formatMoney(computed.extWarrantyTotal));

  // OMVIC Fee + additional fees/accessories via the "other" label/value slots
  // Slot 1: OMVIC Fee (always)
  safeSetText(form, 'settlement_other_label_1', worksheet.omvic_fee ? 'OMVIC FEE' : '');
  safeSetText(form, 'settlement_other_value_1', formatMoneyOrBlank(worksheet.omvic_fee));

  // Slots 2-7: Fees, then Accessories (up to 6 additional line items)
  const extraLineItems: { label: string; amount: number }[] = [];
  
  for (const fee of worksheet.fees) {
    if (fee.name || fee.amount) extraLineItems.push({ label: fee.name || 'Fee', amount: fee.amount });
  }
  for (const acc of worksheet.accessories) {
    if (acc.name || acc.amount) extraLineItems.push({ label: acc.name || 'Accessory', amount: acc.amount });
  }

  for (let i = 0; i < 6; i++) {
    const slotNum = i + 2; // slots 2–7
    const item = extraLineItems[i];
    safeSetText(form, `settlement_other_label_${slotNum}`, item ? item.label.toUpperCase() : '');
    safeSetText(form, `settlement_other_value_${slotNum}`, item ? formatMoneyOrBlank(item.amount) : '');
  }

  // Sub-Total 1
  safeSetText(form, 'settlement_subtotal', formatMoney(computed.subTotal1));

  // Trade-in deduction
  safeSetText(form, 'settlement_trade_in', tradeIn.has_trade_in ? formatMoney(tradeIn.trade_in_allowance) : '');

  // Net Difference
  safeSetText(form, 'settlement_difference', formatMoney(computed.netDifference));

  // HST
  safeSetText(form, 'settlement_hst_on_subtotal', ''); // HST registrants
  safeSetText(form, 'settlement_hst_net_difference', formatMoney(computed.hstAmount)); // Non-registrants
  safeSetText(form, 'settlement_hst_on_trade_in', '');

  // Licence, Gasoline, Payout Lien
  safeSetText(form, 'settlement_license_fee', formatMoney(worksheet.license_fee));
  safeSetText(form, 'settlement_gasoline', formatMoneyOrBlank(worksheet.gasoline));
  safeSetText(form, 'settlement_hst_payout_lien', computed.payoutLien ? formatMoney(computed.payoutLien) : '');

  // Sub-Total 2
  safeSetText(form, 'settlement_hst_sub_total', formatMoney(computed.subTotal2));

  // Deposit
  safeSetText(form, 'settlement_deposit', formatMoney(worksheet.deposit_amount));

  // Deposit method checkboxes
  safeSetCheckbox(form, 'checkbox_terms_settlement_credit_card', worksheet.deposit_method === 'credit_card');
  safeSetCheckbox(form, 'checkbox_terms_settlement_eft', worksheet.deposit_method === 'eft');
  safeSetCheckbox(form, 'checkbox_terms_settlement_cheque', worksheet.deposit_method === 'cheque');
  safeSetCheckbox(form, 'checkbox_terms_settlement_cash', worksheet.deposit_method === 'cash');
  safeSetCheckbox(form, 'checkbox_terms_settlement_debit', worksheet.deposit_method === 'debit');

  // How paid text
  const depositMethodLabels: Record<string, string> = {
    credit_card: 'Credit Card',
    eft: 'EFT',
    cheque: 'Cheque',
    cash: 'Cash',
    debit: 'Debit',
  };
  safeSetText(form, 'settlement_how_paid', depositMethodLabels[worksheet.deposit_method] || '');

  // Payable on Delivery
  safeSetText(form, 'settlement_payable_on_delivery', formatMoney(computed.payableOnDelivery));

  // Insurance add-ons
  safeSetText(form, 'settlement_insurance_life', '');
  safeSetText(form, 'settlement_insurance_disability', '');
  
  // Insurance add-on checkboxes
  safeSetCheckbox(form, 'checkbox_terms_settlement_insurance_life', false);
  safeSetCheckbox(form, 'checkbox_terms_settlement_insurance_loss', false);
  safeSetCheckbox(form, 'checkbox_terms_settlement_insurance_disability', false);
  safeSetCheckbox(form, 'checkbox_terms_settlement_insurance_other', false);

  // RST on Insurance, Lien Registration Fee
  safeSetText(form, 'settlement_rst_on_insurance', formatMoney(worksheet.rst_on_insurance));
  safeSetText(form, 'settlement_lien_registration_fee', formatMoneyOrBlank(worksheet.lien_registration_fee));

  // Total Principal Amount of Loan
  safeSetText(form, 'settlement_total_amount_of_loan', formatMoney(computed.totalPrincipalLoan));

  // Cost of Borrowing
  safeSetText(form, 'settlement_cost_of_borrowing', formatMoneyOrBlank(computed.costOfBorrowing));

  // TOTAL BALANCE DUE (14pt bold in template)
  safeSetText(form, 'settlement_total_balance_due', formatMoney(computed.totalBalanceDue));

  // ── Trade-In Vehicle ──

  if (tradeIn.has_trade_in) {
    safeSetText(form, 'trade_in_year', tradeIn.year);
    safeSetText(form, 'trade_in_make', tradeIn.make.toUpperCase());
    safeSetText(form, 'trade_in_model', tradeIn.model.toUpperCase());
    safeSetText(form, 'trade_in_body_type', tradeIn.body_type.toUpperCase());
    safeSetText(form, 'trade_in_trim_level', tradeIn.trim_level.toUpperCase());
    safeSetText(form, 'trade_in_colour', tradeIn.colour.toUpperCase());

    // Trade-in VIN — sequential trade_in_vin_0 through _16
    const tradeVin = tradeIn.vin.toUpperCase().split('');
    for (let i = 0; i < 17; i++) {
      safeSetText(form, `trade_in_vin_${i}`, tradeVin[i] || '');
    }

    // Trade-in distance — 6 character boxes
    const tradeDistDigits = padDigits(tradeIn.exact_distance, 6);
    for (let i = 0; i < 6; i++) {
      safeSetText(form, `trade_in_distance_${i}`, tradeDistDigits[i]);
    }

    // Distance type
    safeSetCheckbox(form, 'checkbox_trade_in_kms', tradeIn.distance_type === 'kms');
    safeSetCheckbox(form, 'checkbox_trade_in_miles', tradeIn.distance_type === 'miles');

    // HST Registrant No
    safeSetText(form, 'trade_in_hst_no', tradeIn.hst_registrant_no);

    // Lien
    safeSetCheckbox(form, 'checkbox_trade_in_lien_yes', tradeIn.has_lien);
    safeSetCheckbox(form, 'checkbox_trade_in_lien_no', !tradeIn.has_lien);

    if (tradeIn.has_lien) {
      safeSetText(form, 'trade_lien_holder', tradeIn.lien_holder);
      safeSetText(form, 'trade_lien_holder_amount', formatMoney(tradeIn.lien_amount));
    }
  }

  // ── Financing ──

  if (financing.is_financing) {
    safeSetCheckbox(form, 'checkbox_terms_third_party', financing.financing_type === 'third_party');
    safeSetCheckbox(form, 'checkbox_terms_dealer', financing.financing_type === 'dealer');

    safeSetText(form, 'finance_date_of_advance', formatDate(financing.date_of_advance));
    safeSetText(form, 'finance_interest_start_date', formatDate(financing.interest_start_date));
    safeSetText(form, 'finance_regular_payment', financing.regular_payments ? formatMoney(financing.regular_payments) : '');
    safeSetText(form, 'finance_fixed_rate', financing.fixed_rate ? `${financing.fixed_rate}%` : '');
    safeSetText(form, 'finance_term_of_loan', financing.term_of_loan ? String(financing.term_of_loan) : '');
    safeSetText(form, 'finance_amortization_period', financing.amortization_period ? String(financing.amortization_period) : '');
    safeSetText(form, 'finance_total_payments', financing.total_number_of_payments ? String(financing.total_number_of_payments) : '');
    safeSetText(form, 'finance_payment_frequency', financing.payment_frequency || '');
    safeSetText(form, 'finance_date_first_payment', formatDate(financing.date_of_first_payment, true));
    safeSetText(form, 'finance_date_last_payment', financing.date_of_last_payment_amount);
    safeSetText(form, 'finance_remaining_balance_at_term', financing.remaining_balance ? formatMoney(financing.remaining_balance) : '');
    safeSetText(form, 'finance_total_all_payments', financing.total_of_all_payments ? formatMoney(financing.total_of_all_payments) : '');
    safeSetText(form, 'finance_total_interest', financing.total_interest ? formatMoney(financing.total_interest) : '');
    safeSetText(form, 'finance_annual_percentage', financing.apr ? `${financing.apr}%` : '');

    // Dealer incentive
    safeSetCheckbox(form, 'checkbox_terms_incentive_yes', financing.dealer_incentive);
    safeSetCheckbox(form, 'checkbox_terms_incentive_no', !financing.dealer_incentive);
  }

  // ── Comments / Disclosures (9 lines) ──

  const commentLines = comments ? comments.split('\n').filter(l => l.trim()) : [];
  for (let i = 0; i < 9; i++) {
    safeSetText(form, `comments_${i + 1}`, commentLines[i] || '');
  }

  // ── Salesperson & Vendor ──

  // These fields are filled if provided, otherwise left from template
  if (data.salespersonName) {
    safeSetText(form, 'sales_person_name', data.salespersonName);
  }
  if (data.salespersonRegNo) {
    safeSetText(form, 'sales_registration_no', data.salespersonRegNo);
  }

  // Vendor/Acceptor info (static dealership officials)
  safeSetText(form, 'vendor_name', 'Rishab Ahuja');
  safeSetText(form, 'vendor_registration_no', '5886213');
  safeSetText(form, 'vendor_acceptor_registration_no', '5913223');
  safeSetText(form, 'vendor_title', 'Director');
  safeSetText(form, 'vendor_date', formatDate(data.dealDate || new Date().toISOString()));

  // Make company/dealership fields read-only on the preview
  const companyFields = [
    'dealership_name',
    'dealership_address',
    'dealership_phone',
    'dealership_hst_number',
    'dealership_operating_name',
    'vendor_name',
    'vendor_registration_no',
    'vendor_acceptor_registration_no',
    'vendor_title',
    'vendor_date',
    'sales_person_name',
    'sales_person_name_2',
    'sales_registration_no'
  ];

  for (const fieldName of companyFields) {
    try {
      const field = form.getField(fieldName);
      field.enableReadOnly();
    } catch {
      // Field not found — skip silently
    }
  }

  return pdfDoc.save();
}
